import React from 'react'
import { useIntl } from 'react-intl'
import { AITripPreferenceStep } from 'tripPlanner/pages/trip/GenerateTripPage'
import TextButton from 'LuxKit/Button/TextButton'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import SolidSparklesIcon from 'LuxKit/Icons/solid/SolidSparklesIcon'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Sheet from 'components/utils/Sheet'
import zIndex from 'styles/tools/z-index'

const Content = styled(Sheet)`
  grid-column: 1 / -1;
  grid-row: 1 / -1;
  display: flex;
  justify-content: flex-end;
  padding: ${rem(12)} ${rem(20)} ${rem(12)} ${rem(20)};

  ${mediaQueryUp.desktop} {
    padding: ${rem(16)} ${rem(60)};
  }
`

const Background = styled(Sheet)`
  grid-column: 1 / -1;
  grid-row: 1 / -1;
  opacity: 0.7;
  z-index: -1;
`

const Footer = styled(Sheet)`
  height: fit-content;
  width: 100%;

  max-width: ${rem(640)};
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: ${zIndex.stickyFooter};
  display: grid;
  grid-template: 1fr / 1fr;

  ${mediaQueryUp.desktop} {
    max-width: ${rem(720)};
    /* position: static;
    margin-left: 0;
    margin-right: 0; */
  }
`

interface Props {
  step: AITripPreferenceStep
  isAnyThemeSelected: boolean
  isAnyMustDoActivitySelected: boolean
  isAnyFoodStyleOrDietRequirementSelected: boolean
  isGeneratingTrip: boolean
  isLocationSelected: boolean
  isOccupancySelected: boolean
  isDatePickerSelected: boolean
  isSurpriseMe?: boolean
  handleClick: () => void
  handleGenerateTrip: () => void
}

function GenerateTripFooter(props: Props) {
  const intl = useIntl()
  const { step, isAnyThemeSelected, isAnyMustDoActivitySelected, isAnyFoodStyleOrDietRequirementSelected, isGeneratingTrip, handleClick, isLocationSelected, isOccupancySelected, isDatePickerSelected, isSurpriseMe, handleGenerateTrip } = props

  const nextButtonText = intl.formatMessage({
    id: 'cUBfaTXbUt',
    defaultMessage: 'Next',
    description: 'Button text to go to next step',
  })

  const skipButtonText = intl.formatMessage({
    id: 'uc9zTFZwll',
    defaultMessage: 'Skip',
    description: 'Button text to skip current step',
  })

  const createItineraryText = intl.formatMessage({
    id: 'inziQqsKZF',
    defaultMessage: 'Create my itinerary',
    description: 'Button text to create itinerary',
  })

  return (
    <Footer paletteType="inverse" background="neutral-eight">
      <Background />
      <Content>
        {step === 'location' && isLocationSelected && <TextButton
          kind="primary"
          size="large"
          endIcon={<LineAngleRightIcon />}
          onClick={handleClick}
          disabled={isGeneratingTrip}
        >
          {nextButtonText}
        </TextButton>}
        {step === 'occupancy' && isOccupancySelected && <TextButton
          kind="primary"
          size="large"
          endIcon={<LineAngleRightIcon />}
          disabled={isGeneratingTrip}
          type="submit"
          form="occupancy-form"
        >
          {nextButtonText}
        </TextButton>}
        {step === 'datePicker' && isDatePickerSelected && <TextButton
          kind="primary"
          size="large"
          startIcon={isSurpriseMe ? <SolidSparklesIcon /> : undefined}
          endIcon={isSurpriseMe ? undefined : <LineAngleRightIcon />}
          onClick={isSurpriseMe ? handleGenerateTrip : handleClick}
          disabled={isGeneratingTrip}
        >
          {isSurpriseMe ? createItineraryText : nextButtonText}
        </TextButton>}
        {step === 'theme' && <TextButton
          kind={isAnyThemeSelected ? 'primary' : 'secondary'}
          size="large"
          endIcon={<LineAngleRightIcon />}
          onClick={handleClick}
          disabled={isGeneratingTrip}
        >
          {isAnyThemeSelected ? nextButtonText : skipButtonText}
        </TextButton>}

        {(step === 'mustDo' || step.startsWith('mustDo')) && <TextButton
          kind={isAnyMustDoActivitySelected ? 'primary' : 'secondary'}
          size="large"
          endIcon={<LineAngleRightIcon />}
          onClick={handleClick}
          disabled={isGeneratingTrip}
        >
          {isAnyMustDoActivitySelected ? nextButtonText : skipButtonText}
        </TextButton>}

        {step === 'foodStyle' && <TextButton
          kind={isAnyFoodStyleOrDietRequirementSelected ? 'primary' : 'secondary'}
          size="large"
          endIcon={<LineAngleRightIcon />}
          onClick={handleClick}
          disabled={isGeneratingTrip}
        >
          {isAnyFoodStyleOrDietRequirementSelected ? nextButtonText : skipButtonText}
        </TextButton>}
      </Content>
    </Footer>
  )
}

export default GenerateTripFooter
